import React, {useState, useEffect} from 'react';
import {Navigate, NavLink, useSearchParams} from "react-router-dom";
import axios from 'axios';
import styled from 'styled-components';
import LoggedLayout from "layouts/LoggedLayout";
import PopupLayout from "layouts/PopupLayout";
import {getCommunity} from 'helpers/community';
import {authUserPermission} from 'helpers/community';
import Skeleton from 'react-loading-skeleton';

const Rule = styled.div`
	display: flex;
	align-items: center;
	padding: 10px;
	background: var(--bs-light);
	border-radius: 5px;
	margin-bottom: 10px;

	&:last-child {
		margin-bottom: 0;
	}

	.year {
		background: var(--bs-gray-500);
		color: var(--bs-light);
		padding: 2.5px 5px;
		border-radius: 5px;
		font-size: 12px;
		margin-right: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		align-self: stretch;
	}

	.description {
		.type {
			margin-right: 10px;
			font-weight: 500;
			font-size: 13px;
		}

		.details {
			font-size: 11px;
			color: var(--bs-gray-600);

			b {
				font-weight: 400;
				color: var(--bs-dark);
			}
		}
	}

	.actions {
		margin-left: auto;

		button {
			display: inline-flex;
			align-items: center;
			justify-content: center;

			i {
				transform: scale(1.5);
			}
		}

		a {
			margin-left: 8px;
		}
	}
`;

let axiosCancelToken = null;

export default function BankReconciliationRules() {
	const [queryParams] = useSearchParams();
	
	const popup = queryParams.get('popup') === 'true';
	
	let community = getCommunity();

	let [rules, setRules] = useState(null);
	let [skeletonRows, setSkeletonRows] = useState(5);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
	
		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		const getRules = async () => {
			await axios.get('/api/manager/bank-reconciliation-rules/list', {
				params: {
					community_id: community?.id,
					no_paginate: true
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
		    	setRules([...response.data]);
		    	setSkeletonRows(response.data.length);
		  	}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});	
		}

		getRules();
	}, [community?.id]);

	const setRuleActiveStatus = async (idx, status) => {
		await axios.post('/api/manager/bank-reconciliation-rules/update-active/' + rules[idx].id, {
			status: status,
			community_id: community?.id
		}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let updatedRule = response.data.rule;
			let newRules = [...rules];
			newRules[idx] = updatedRule;
			setRules(newRules);
		}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});
	}

	if ( !community?.id ) return <Navigate to="/" />;

	const Layout = popup ? PopupLayout : LoggedLayout;

	return (
		<Layout>
			<section>
				<div className="page-title">
					<h1>Reglas de conciliación automáticas</h1>
					
					<div className="ms-auto">
						{/* <NavLink to="/bank-reconciliation" className="btn btn-sm btn-warning ms-3">Ir a conciliación bancaria</NavLink> */}

						{authUserPermission('add') &&
							<NavLink to={`/bank-reconciliation/rules/add` + (popup ? '?popup=true' : '')} className="btn btn-sm btn-light ms-3">Nueva regla</NavLink>						
						}
					</div>
				</div>

				<div className="page-content">
					<div className="card border-0 shadow-sm">
						<div className="card-body">
							{(rules && rules.length > 0) && rules.map((rule, index) => {
								return (
									<Rule key={index} className="rule">
										<div className="year">
											{rule.year}
										</div>

										<div className="description">
											<div className="type">
												{rule.type === 'counterpart' && 'Contrapartidas'}
												{rule.type === 'bankremittance' && 'Remesa bancaria'}
											</div>
											<div className="details">
												{rule.type === 'counterpart' && 
													<>
														<div><b>Término de búsqueda:</b> {rule.data.term}</div>
														<div>
															<b>Contrapartidas:</b>
															<ul className="mb-0">
																{rule.data?.counterparts?.map((item, idx) => {
																	return (
																		<li key={idx}>
																			{item.counterpart.name_full_path} ({item.percent}%)
																		</li>
																	);
																}
																)}
															</ul>
														</div>
													</>
												}
												{rule.type === 'bankremittance' && <span><b>Término de búsqueda:</b> {rule.data.term}</span>}
											</div>
										</div>
										
										<div className="actions">
											{authUserPermission('edit') &&
												<>
													{rule.active === 1 &&
														<button className="btn btn-sm btn-danger text-white" onClick={() => setRuleActiveStatus(index, false)}><i className="bi bi-stop-fill"></i></button>
													}
													{rule.active === 0 &&
														<button className="btn btn-sm btn-success text-white" onClick={() => setRuleActiveStatus(index, true)}><i className="bi bi-play-fill"></i></button>
													}
													<NavLink to={'/bank-reconciliation/rules/edit/' + rule.id + (popup ? '?popup=true' : '')} className="btn btn-sm btn-link"><i className="bi bi-pencil"></i></NavLink>
												</>
											}
										</div>
										
									</Rule>
								);
							})}
							{rules && rules.length === 0 && <p className="p-0 m-0">No hay reglas de conciliación automáticas</p>}
							{rules === null && <Skeleton count={skeletonRows} height={20} />}
						</div>
					</div>
				</div>
			</section>
		</Layout>
	);
}


